import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { TopNav, Footer } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, ClipboardCheck } from "lucide-react";

const SliderRow = ({ label, value, set, testid }) => (
  <div>
    <div className="flex justify-between">
      <Label className="text-sm text-slate-700">{label}</Label>
      <span className="text-sm font-medium text-purple-900">{value}/10</span>
    </div>
    <Slider data-testid={testid} className="mt-3" value={[value]} onValueChange={(v) => set(v[0])} min={1} max={10} step={1} />
  </div>
);

export default function Followup() {
  const { clientId: assessmentId } = useParams();
  const navigate = useNavigate();
  const [energy, setEnergy] = useState(5);
  const [sleep, setSleep] = useState(5);
  const [stress, setStress] = useState(5);
  const [shedding, setShedding] = useState(5);
  const [digestion, setDigestion] = useState(5);
  const [progress, setProgress] = useState("");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (!progress) return toast.error("Please rate your overall progress");
    setLoading(true);
    try {
      await api.post(`/assessments/${assessmentId}/followup`, {
        energy_level: energy,
        sleep_quality: sleep,
        stress_level: stress,
        shedding_level: shedding,
        digestion,
        overall_progress: progress,
        notes,
      });
      toast.success("Follow-up submitted. Thank you.");
      navigate(`/report/${assessmentId}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submission failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <TopNav />
      <main className="flex-1 max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12 w-full">
        <div className="kcis-card p-7 sm:p-10 kcis-fade-up">
          <div className="h-12 w-12 rounded-2xl bg-purple-50 text-purple-900 flex items-center justify-center mb-5">
            <ClipboardCheck className="h-5 w-5" />
          </div>
          <div className="kcis-overline text-purple-900">7-day check-in</div>
          <h1 className="font-display text-3xl tracking-tight text-slate-900 mt-2">How is your week unfolding?</h1>
          <p className="text-sm text-slate-600 mt-2">Rate honestly — your patterns help shape the next phase.</p>

          <div className="mt-8 space-y-6">
            <SliderRow label="Energy" value={energy} set={setEnergy} testid="fu-energy" />
            <SliderRow label="Sleep quality" value={sleep} set={setSleep} testid="fu-sleep" />
            <SliderRow label="Stress" value={stress} set={setStress} testid="fu-stress" />
            <SliderRow label="Hair shedding (lower is better)" value={shedding} set={setShedding} testid="fu-shedding" />
            <SliderRow label="Digestion" value={digestion} set={setDigestion} testid="fu-digestion" />

            <div>
              <Label className="text-sm text-slate-700">Overall progress</Label>
              <Select value={progress} onValueChange={setProgress}>
                <SelectTrigger className="mt-2 rounded-xl border-slate-200 bg-slate-50/60 h-12" data-testid="fu-progress">
                  <SelectValue placeholder="Choose..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="significantly_improved">Significantly improved</SelectItem>
                  <SelectItem value="improved">Improved</SelectItem>
                  <SelectItem value="same">About the same</SelectItem>
                  <SelectItem value="declined">Slightly declined</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-sm text-slate-700">Notes (optional)</Label>
              <Textarea
                data-testid="fu-notes"
                className="mt-2 rounded-xl border-slate-200 bg-slate-50/60 min-h-[110px]"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="What surprised you, what felt challenging?"
              />
            </div>

            <Button onClick={submit} disabled={loading} className="w-full rounded-full bg-purple-900 hover:bg-purple-800 text-white h-12" data-testid="fu-submit">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit follow-up"}
            </Button>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
